import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  REACT_APP_SITE_URL,
  SITE_DESCRIPTION,
  SITE_IMAGE,
  DEFAULT_KEYWORDS,
} from "../config/seo";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  article?: boolean;
  /** ISO date string — required for stable prerendered output; never defaults to "now" */
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
  keywords?: string[];
  /** Extra JSON-LD blocks (Product, FAQPage, LocalBusiness, …) rendered alongside the default WebPage/Article block */
  jsonLd?: object[];
}

export const SEO: React.FC<SEOProps> = ({
  title = SITE_NAME,
  description = SITE_DESCRIPTION,
  image = SITE_IMAGE,
  article = false,
  publishedTime,
  modifiedTime,
  noindex = false,
  keywords = DEFAULT_KEYWORDS,
  jsonLd = [],
}) => {
  const { pathname } = useLocation();
  const url = `${REACT_APP_SITE_URL}${pathname}`;
  const pageTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const ogImage = image.startsWith("http")
    ? image
    : `${REACT_APP_SITE_URL}${image}`;

  const defaultJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": article ? "Article" : "WebPage",
      name: pageTitle,
      ...(article ? { headline: title } : {}),
      description,
      url,
      image: ogImage,
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: `${REACT_APP_SITE_URL}/logo.png`,
        },
      },
      ...(article && publishedTime
        ? {
            datePublished: publishedTime,
            ...(modifiedTime ? { dateModified: modifiedTime } : {}),
            author: {
              "@type": "Organization",
              name: SITE_NAME,
            },
          }
        : {}),
    }),
    [pageTitle, title, description, url, ogImage, article, publishedTime, modifiedTime],
  );

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta
        name="robots"
        content={
          noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
        }
      />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      {article && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {article && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Performance Hints */}
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />

      {/* JSON-LD (string children — react-helmet-async does not render
          dangerouslySetInnerHTML on script tags) */}
      <script type="application/ld+json">{JSON.stringify(defaultJsonLd)}</script>
      {jsonLd.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
